import { SurveyModel } from 'survey-core';
import Showdown from 'showdown';
import { WAIVER_QUESTION_PREFIX } from '@/lib/waivers';

const converter = new Showdown.Converter({
  openLinksInNewWindow: true,
});

const VALID_US_STATES = new Set([
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
  'AS',
  'DC',
  'GU',
  'MP',
  'PR',
  'TT',
  'VI',
]);

const VALID_CANADIAN_PROVINCES = new Set([
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

/**
 * Survey-instance handlers shared by every brand/country JSON: markdown in
 * survey text, the PO-box and state/province validators, and the CSS hook
 * for the typed-signature fields.
 */
export const prepareForSurvey = (thisSurvey: SurveyModel) => {
  // add custom css classes
  thisSurvey.onUpdateQuestionCssClasses.add((_survey, options) => {
    if (options.question.name === 'signature' || options.question.name === 'minorSignature') {
      options.cssClasses.root += ' signatureInput';
    }
    if (options.question.name.startsWith(WAIVER_QUESTION_PREFIX)) {
      options.cssClasses.root += ' waiverText';
    }
    if (options.question.name === 'optInLegalNotice') {
      options.cssClasses.root += ' optInDisclaimer';
    }
  });

  // add markdown support
  thisSurvey.onTextMarkdown.add(function (_survey, options) {
    //convert the markdown text to html
    let str = converter.makeHtml(options.text);
    //remove root paragraphs <p></p>
    str = str.substring(3);
    str = str.substring(0, str.length - 4);
    //set html
    options.html = str;
  });

  thisSurvey.onValidateQuestion.add(function (survey, options) {
    if (options.question.name === 'address1') {
      const addressData = survey.data['address_group'];
      const address1Value = addressData?.address1 || options.value;

      if (address1Value) {
        const poBoxRegEx = /^(p[s|.|,]*o[s|.|,]*|post.*office|box\s*\d+)/i;
        const normalizedAddress = address1Value.toLowerCase().replace(/\s/g, '');

        if (poBoxRegEx.test(normalizedAddress)) {
          const locale = survey.locale || 'en';
          const errorMessages: Record<string, string> = {
            en: 'PO Boxes are not allowed',
            es: 'No se permiten apartados postales',
            fr: 'Les boîtes postales ne sont pas autorisées',
          };
          options.error = errorMessages[locale] || errorMessages.en;
        }
      }
    }

    if (options.question.name === 'state') {
      const addressData = survey.data['address_group'];
      const addressGroupQuestion = survey.getQuestionByName('address_group');
      const addressGroupCountry =
        (addressGroupQuestion as any)?.country || (addressGroupQuestion as any)?.jsonObj?.country;
      const surveyCountryCode = survey.data['country_code'];
      const country =
        addressData?.country ||
        survey.data['country'] ||
        (surveyCountryCode === 'CAN' ? 'ca' : undefined) ||
        addressGroupCountry ||
        'us';

      if (addressData && addressData.state) {
        const stateValue = addressData.state;
        const resolvedCountry = country.toLowerCase();
        let validStates: Set<string>;
        if (resolvedCountry === 'ca' || resolvedCountry === 'can') {
          // Canadian survey — accept both Canadian provinces and US states
          // since American visitors may fill out the form with a US address
          validStates = new Set([...VALID_US_STATES, ...VALID_CANADIAN_PROVINCES]);
        } else {
          validStates = VALID_US_STATES;
        }

        if (!validStates.has(stateValue.toUpperCase())) {
          options.error = 'Please enter a valid state/province abbreviation.';
        }
      }
    }
  });
};

// Helper function to validate and normalize state abbreviations
export const validateStateAbbreviation = (stateInput: string, country: string): string => {
  if (!stateInput) return '';

  // Convert to uppercase and trim
  const state = stateInput.trim().toUpperCase();
  const normalizedCountry = (country || '').toLowerCase();

  // For US and Canada, ensure it's exactly 2 characters and valid
  if (normalizedCountry === 'us' || normalizedCountry === 'ca') {
    const validStates = normalizedCountry === 'us' ? VALID_US_STATES : VALID_CANADIAN_PROVINCES;

    if (state.length === 2 && validStates.has(state)) {
      return state;
    }

    console.warn(`Invalid state abbreviation: "${stateInput}". This will trigger form validation.`);
    return '';
  }

  return state;
};

/**
 * Attaches Google Places Autocomplete to the address composite's street
 * input once it renders. survey-core does not do this itself (see its
 * consumer guide §12); the loader script lives in index.html.
 */
export const prepareSurveyOnQuestionAdded = (_creator: unknown, options: { survey: SurveyModel }) => {
  options.survey!.onAfterRenderQuestion.add((survey, questionOptions) => {
    if (questionOptions.question.name === 'address1') {
      // Get the parent composite question through the survey
      const compositeQuestion = questionOptions.question.survey.getQuestionByName('address_group');
      const country = (compositeQuestion as any)?.jsonObj?.country || 'us';

      const inputEl = (questionOptions.htmlElement as HTMLElement)?.querySelector?.('input');
      if (!inputEl) return;

      const attachAutocomplete = () => {
        // Don't attach if input was removed from DOM (user navigated away)
        if (!document.contains(inputEl)) return;

        const autocomplete = new google.maps.places.Autocomplete(inputEl, {
          types: ['address'],
          componentRestrictions: {
            country: [country],
          },
          fields: ['address_components', 'formatted_address'],
          ...(questionOptions.question as any).addressAutocompleteConfig,
        });

        autocomplete.addListener('place_changed', async function () {
          const place = await autocomplete.getPlace();

          const ParsedData: Record<string, any> = {
            formatted_address: place.formatted_address,
          };

          const postalData = place.address_components?.find(item => item.types.includes('postal_code'));
          const countryData = place.address_components?.find(item => item.types.includes('country'));
          const addressData = place.address_components?.find(item =>
            item.types.includes('administrative_area_level_1'),
          );
          const cityData = place.address_components?.find(item => item.types.includes('locality'));
          const routeData = place.address_components?.find(item => item.types.includes('route'));
          const streetNumberData = place.address_components?.find(item => item.types.includes('street_number'));

          ParsedData.address1 = [streetNumberData?.long_name, routeData?.long_name].join(' ').trim();
          ParsedData.city = cityData == null ? '' : cityData.long_name;

          const rawState = addressData?.short_name || '';
          const parsedCountry = countryData?.short_name || country;
          ParsedData.state = validateStateAbbreviation(rawState, parsedCountry);

          ParsedData.zip_code = postalData == null ? '' : postalData.long_name;
          ParsedData.country = countryData == null ? '' : countryData.short_name;

          const isComposite = survey.getQuestionByName('address_group');
          if (isComposite) {
            survey.setValue('address_group', ParsedData);
          } else {
            ['address1', 'city', 'state', 'zip_code', 'country'].forEach(key => {
              try {
                survey.setValue(key, ParsedData[key]);
              } catch (e) {
                console.log('error', e);
              }
            });
          }
        });
      }; // end attachAutocomplete

      if (typeof google !== 'undefined' && google?.maps?.places?.Autocomplete) {
        attachAutocomplete();
      } else {
        // Google Maps loads async — poll until ready or give up after 10s (handles private browsing)
        let attempts = 0;
        const interval = setInterval(() => {
          attempts++;
          if (typeof google !== 'undefined' && google?.maps?.places?.Autocomplete) {
            clearInterval(interval);
            attachAutocomplete();
          } else if (attempts >= 50) {
            clearInterval(interval);
          }
        }, 200);
      }
    }
  });
};
